// @flow
import type { ChildrenArray, Element } from 'react';
import React, { Fragment } from 'react';

export const RowContext = React.createContext<{|
  availableColumns: number,
  pixelWidth: number,
|}>({
  availableColumns: 30,
  pixelWidth: 716,
});

const COLUMN_WIDTH = 20;
const GUTTER_WIDTH = 4;

type RowProps = {|
  children: ChildrenArray<
    // We need to support <Fragment><ColumnInternal></Fragment> but
    // not sure we can encode that here. If we move to TS we can't
    // even typecheck Element types at all...
    Element<typeof ColumnInternal> | Element<typeof Fragment>,
  >,
  style?: {},
|};

export function RowInternal(props: RowProps) {
  return (
    <div style={{ ...props.style, display: 'table' }}>
      {interleaveElement(<GutterInternal />, props.children)}
    </div>
  );
}
type ColumnProps =
  | {|
      colSpan: number,
      children?: React$Node,
    |}
  | {| children?: React$Node, fullWidth: boolean |};

export function ColumnInternal(props: ColumnProps) {
  const { availableColumns, pixelWidth } = React.useContext(RowContext);
  const columnWidth = props.colSpan
    ? getPixelWidthFromColumns(props.colSpan)
    : pixelWidth;
  return (
    <RowContext.Provider
      value={{
        availableColumns: props.colSpan || availableColumns,
        pixelWidth: columnWidth,
      }}
    >
      <div
        style={{
          width: columnWidth,
          display: 'table-cell',
          // Ensure that all ColumnInternals in a given line box are aligned
          // with the top of said line box, rather than the baseline.
          verticalAlign: 'top',
          position: 'relative',
        }}
      >
        {props.children}
      </div>
    </RowContext.Provider>
  );
}

export function getPixelWidthFromColumns(columns: number) {
  if (columns <= 0)
    throw new RangeError('ColumnInternal should take a colSpan > 0');
  return (
    COLUMN_WIDTH * columns + (columns > 1 ? GUTTER_WIDTH * (columns - 1) : 0)
  );
}

export function useFullAvailableWidth() {
  const { pixelWidth } = React.useContext(RowContext);
  return pixelWidth;
}

function interleave(x, ys) {
  const result = [];
  for (let i = 0; i < ys.length; i++) {
    result.push(ys[i]);
    if (i !== ys.length - 1) result.push(x);
  }
  return result;
}

function interleaveElement(element, elements) {
  const elementsArray = React.Children.toArray(elements);
  return interleave(element, elementsArray).map((el, key) =>
    React.cloneElement(el, { key }),
  );
}

export function GutterInternal() {
  return <div style={{ width: GUTTER_WIDTH, display: 'table-cell' }} />;
}
